import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowUpRight, Heart, HandHelping, Home as HomeIcon, Sprout, Users, MapPin } from "lucide-react";
import { CATEGORY_ARTICLES } from "@/data/categoryArticles";
import { useGeo } from "@/lib/useGeo";
import ThingsToKnowStrip from "@/components/ThingsToKnowStrip";
import InMemoriamStrip from "@/components/InMemoriamStrip";
import WorldChangerOfTheDay from "@/components/WorldChangerOfTheDay";
import FeaturedPulseStrip from "@/components/FeaturedPulseStrip";
import EventsWeekendStrip from "@/components/EventsWeekendStrip";
import FeaturedFamilyTreeStrip from "@/components/FeaturedFamilyTreeStrip";
import FamilyAnniversariesStrip from "@/components/FamilyAnniversariesStrip";
import ArticleOfTheWeek from "@/components/ArticleOfTheWeek";
import HomeCallout from "@/components/HomeCallout";
import LocalClockWeather from "@/components/LocalClockWeather";
import QuoteOfTheDayBanner from "@/components/QuoteOfTheDayBanner";
import AutoDetectLocationBanner from "@/components/AutoDetectLocationBanner";
import SocialCapitalEcosystem from "@/components/SocialCapitalEcosystem";
import TodaysStarWidget from "@/components/TodaysStarWidget";
import LivePulseTicker from "@/components/LivePulseTicker";
import SweepTicker from "@/components/SweepTicker";
import SeoHead from "@/components/SeoHead";
import SectionTagline from "@/components/SectionTagline";
import { BRAND } from "@/lib/siteSeo";

const CATEGORIES = [
  { key: "housing", label: "Housing & Shelter", icon: HomeIcon, blurb: "Repair, build, roof, or retrofit safe homes." },
  { key: "food", label: "Food & Basics", icon: HandHelping, blurb: "Meals, groceries, hygiene, clean water." },
  { key: "education", label: "Education & Supplies", icon: Users, blurb: "Tutoring, scholarships, mentoring, supplies." },
  { key: "medical", label: "Medical Care & Prescription", icon: Heart, blurb: "Surgeries, medicines, transportation, clinics." },
  { key: "seniors", label: "Senior Care & Childcare", icon: Users, blurb: "Companionship, shopping, transport, home visits." },
  { key: "environment", label: "Community & Environment", icon: Sprout, blurb: "Tree planting, cleanups, community gardens." },
];

const IMG_HERO = "";
const IMG_STORY = "";
const IMG_AID = "https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_HOUSE = "";

export default function Home() {
  const [impact, setImpact] = useState(null);
  const [stories, setStories] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [housing, setHousing] = useState([]);
  const { geo } = useGeo();

  useEffect(() => {
    http.get("/impact/global").then((r) => setImpact(r.data)).catch(() => {});
    http.get("/stories").then((r) => setStories(r.data.slice(0, 3))).catch(() => {});
    http.get("/organizations").then((r) => setOrgs(r.data.slice(0, 4))).catch(() => {});
    http.get("/housing-projects").then((r) => setHousing(r.data.slice(0, 3))).catch(() => {});
    // Background health-check — if travel articles/other content queues
    // are non-empty, backend spawns a small background batch to keep
    // profiles filling in. Throttled server-side to once per 15 min.
    http.get("/travel/health").catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SeoHead section="home" url="https://socialpulse.com/" />
      {/* AUTO-DETECT LOCATION — offer to set locality from IP-detected geo */}
      <AutoDetectLocationBanner />

      {/* LIVE PULSE — unified real-time community activity ticker */}
      <LivePulseTicker />

      {/* QUOTE OF THE DAY — placed at the very top for immediate visibility */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6">
        <QuoteOfTheDayBanner />
        <div className="mt-3 flex justify-center">
          <SweepTicker />
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 wofy-reveal">
            {geo?.country_name && (
              <div
                data-testid="geo-welcome-pill"
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-wider mb-4"
                style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)", border: "1px solid var(--wofy-border)" }}
                title={`Detected from your IP · ${geo.country_calling_code || ""}`}
              >
                <MapPin className="w-3 h-3" style={{ color: "var(--wofy-primary)" }} />
                Welcome from {geo.country_name}
                {geo.country_calling_code && (
                  <span className="opacity-60">· {geo.country_calling_code}</span>
                )}
              </div>
            )}
            <LocalClockWeather />
            <div className="overline mb-6">A GLOBAL SOCIAL IMPACT PLATFORM</div>
            <h1
              className="font-display tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl mb-4"
              data-testid="home-h1"
            >
              {BRAND.home_h1}
            </h1>
            <h2
              className="text-base md:text-lg leading-relaxed max-w-xl mb-6"
              data-testid="home-h2"
              style={{ color: "var(--wofy-text-soft)" }}
            >
              {BRAND.home_h2}
            </h2>
            <div className="max-w-xl space-y-5 text-base leading-relaxed" style={{ color: "var(--wofy-text)" }}>
              <p>
                The greatest joy in life comes from helping others. Social Pulse connects those who want to help with those who genuinely need it &mdash; regardless of religion, nationality, race, gender or affiliation.
              </p>
              <p style={{ color: "var(--wofy-text-soft)" }}>
                Social Pulse is a growing movement dedicated to reducing food waste, improving food access, and connecting communities through technology, collaboration, and compassion. Learn more at{" "}
                <a
                  href="https://www.socialpulse.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2"
                  style={{ color: "var(--wofy-primary)" }}
                >
                  socialpulse.com
                </a>.
              </p>
              <div
                className="rounded-2xl border px-5 py-4 text-sm"
                style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
                data-testid="hero-contact-block"
              >
                <div className="overline mb-2">CONTACT</div>
                <div className="font-medium" style={{ color: "var(--wofy-text)" }}>Thomas (Tom) Vellaringattu</div>
                <a
                  href="mailto:support@socialpulse.com"
                  className="mt-1 inline-block underline underline-offset-2"
                  style={{ color: "var(--wofy-primary)" }}
                >
                  support@socialpulse.com
                </a>
              </div>
            </div>
            <div className="mt-6">
              <Link
                to="/vision-mission"
                data-testid="hero-vision-mission-link"
                className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4"
                style={{ color: "var(--wofy-primary)" }}
              >
                Social Pulse Vision &amp; Mission <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/offer-help" data-testid="hero-offer-btn" className="wofy-btn-primary">Offer Help <ArrowUpRight className="w-4 h-4" /></Link>
              <Link to="/find-help" data-testid="hero-find-btn" className="wofy-btn-ghost">Find Help</Link>
              <Link to="/opportunities" data-testid="hero-volunteer-btn" className="text-sm underline underline-offset-4 ml-2" style={{ color: "var(--wofy-text-soft)" }}>Volunteer near you →</Link>
            </div>

            <HomeCallout />

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {[
                ["Volunteers", impact?.volunteers ?? "—"],
                ["Organizations", impact?.organizations ?? "—"],
                ["Hours served", impact?.hours_served ?? "—"],
                ["Countries", impact?.countries ?? "—"],
              ].map(([k, v]) => (
                <div key={k} data-testid={`impact-${k}`}>
                  <div className="font-display text-3xl">{v}</div>
                  <div className="overline mt-1">{k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative wofy-reveal" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-3xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
              <img src={IMG_HERO} alt="Volunteers helping community" className="w-full h-[380px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(28,27,26,0.45) 100%)" }} />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline text-white/80 mb-2">STORY OF THE WEEK</div>
                <p className="font-display text-white text-2xl leading-snug">&ldquo;They gave me a roof. Now I bake for every one of them.&rdquo;</p>
                <p className="text-white/80 text-xs mt-2 font-mono tracking-wide">— GRANDMOTHER RUTH, LEEDS</p>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 rounded-2xl border p-4 hidden md:flex flex-col justify-between"
                 style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}>
              <div className="overline">TODAY</div>
              <div>
                <div className="font-display text-3xl">{impact?.volunteer_sessions ?? 0}</div>
                <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>volunteer sessions</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THINGS TO KNOW — daily briefing strip (moved up for visibility) */}
      <ThingsToKnowStrip />

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3">CATEGORIES OF HELP</div>
            <h2 className="font-display text-3xl md:text-4xl max-w-xl">Every kind of kindness has a home here.</h2>
          </div>
          <Link to="/find-help" className="hidden md:inline text-sm underline underline-offset-4">Browse all →</Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => {
            const hasArticle = Boolean(CATEGORY_ARTICLES[c.key]);
            return (
              <div key={c.key} data-testid={`category-${c.key}`} className="wofy-card group relative flex flex-col">
                <Link
                  to={`/find-help?category=${c.key}`}
                  aria-label={`Find help — ${c.label}`}
                  className="absolute inset-0 rounded-2xl"
                  data-testid={`category-${c.key}-find`}
                />
                <div className="relative flex items-start justify-between">
                  <c.icon className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="relative mt-6 flex items-baseline justify-between gap-3">
                  <div className="font-display text-2xl">{c.label}</div>
                  {hasArticle && (
                    <Link
                      to={`/categories/${c.key}`}
                      data-testid={`category-${c.key}-learn-more`}
                      className="relative z-10 text-xs font-mono uppercase tracking-wider whitespace-nowrap hover:underline underline-offset-4"
                      style={{ color: "var(--wofy-primary)" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Learn more &raquo;
                    </Link>
                  )}
                </div>
                <p className="relative mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{c.blurb}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* THIS WEEKEND NEAR YOU — geo-filtered community events */}
      <EventsWeekendStrip limit={8} />

      {/* IN MEMORIAM — Departed this year strip */}
      <InMemoriamStrip limit={8} />

      {/* WORLD CHANGER OF THE DAY */}
      <WorldChangerOfTheDay />

      {/* FEATURED PULSE — admin-curated posts (silently hides when none) */}
      <FeaturedPulseStrip />

      {/* FAMILY TREE OF THE WEEK */}
      <FeaturedFamilyTreeStrip />

      {/* THIS WEEK IN YOUR FAMILY (only renders for signed-in users with events in window) */}
      <FamilyAnniversariesStrip windowDays={14} />

      {/* ARTICLE OF THE WEEK */}
      <ArticleOfTheWeek />

      {/* TODAY'S STAR — a daily featured species/place from Nature Atlas */}
      <TodaysStarWidget />

      {/* SOCIAL CAPITAL ECOSYSTEM — colorful 10-vertical infographic
          + progression showing how every positive action compounds. */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SocialCapitalEcosystem />
      </section>

      {/* HOUSING PROJECTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline mb-3">HOUSING</div>
            <h2 className="font-display text-3xl md:text-4xl">One family, one home, one legacy.</h2>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
              Every home is tracked — photos, progress, cost, families helped, sponsors. Sponsor a build during your lifetime.
            </p>
            <Link to="/housing" className="mt-6 inline-flex wofy-btn-ghost">See all projects</Link>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-2 gap-6">
            {housing.map((h) => (
              <Link to={`/housing/${h.id}`} data-testid={`housing-card-${h.id}`} key={h.id} className="wofy-card group !p-0 overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={h.image_url || IMG_HOUSE} alt={h.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="overline">{h.city.toUpperCase()}, {h.country.toUpperCase()}</div>
                    <span className="font-mono text-xs">{h.progress}%</span>
                  </div>
                  <div className="font-display text-xl mt-2">{h.title}</div>
                  <div className="mt-3 h-1.5 w-full rounded-full" style={{ background: "var(--wofy-surface)" }}>
                    <div className="h-full rounded-full" style={{ width: `${h.progress}%`, background: "var(--wofy-primary)" }} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ORGS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3">VERIFIED ORGANIZATIONS</div>
            <h2 className="font-display text-3xl md:text-4xl max-w-xl">Trust, verified.</h2>
          </div>
          <Link to="/organizations" className="hidden md:inline text-sm underline underline-offset-4">See directory →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {orgs.map((o) => (
            <Link key={o.id} to={`/organizations/${o.id}`} data-testid={`org-card-${o.id}`} className="wofy-card">
              <div className="overline">{o.verification_tier.toUpperCase()} · {o.years_active}Y</div>
              <div className="font-display text-xl mt-3 leading-snug">{o.name}</div>
              <p className="text-sm mt-3 line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{o.mission}</p>
              <div className="mt-5 flex items-center justify-between text-xs font-mono">
                <span>{o.city.toUpperCase()}</span>
                <span>★ {o.rating}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STORIES + CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-6">
          <div className="overline">STORIES</div>
          <h2 className="font-display text-3xl md:text-4xl">Real people. Real change.</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            {stories.map((s) => (
              <article key={s.id} className="wofy-card !p-0 overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.image_url || IMG_STORY} alt={s.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="overline">{s.category.toUpperCase()}</div>
                  <div className="font-display text-xl mt-2 leading-snug">{s.title}</div>
                  <p className="text-sm mt-2 line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>{s.body}</p>
                  <div className="mt-4 text-xs font-mono">— {s.author_name.toUpperCase()}</div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-5 rounded-3xl border p-8 lg:p-10 relative overflow-hidden"
               style={{ background: "var(--wofy-primary)", color: "#FDFBF7", borderColor: "var(--wofy-primary)" }}>
          <div className="overline text-white/70">JOIN THE MOVEMENT</div>
          <h3 className="font-display text-3xl mt-3 leading-tight">Build your legacy while you&rsquo;re still here to see it.</h3>
          <p className="mt-4 text-sm leading-relaxed text-white/85">
            Earn a Social Capital score for every verified act — hours, mentoring, donations, environmental work. Shareable, recruiter-friendly, and dignifying.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="cta-join" className="rounded-full px-6 py-3 text-sm font-medium" style={{ background: "#FDFBF7", color: "var(--wofy-text)" }}>Create account</Link>
            <Link to="/opportunities" className="rounded-full px-6 py-3 text-sm font-medium border border-white/40 text-white hover:bg-white/10 transition-colors">Volunteer today</Link>
          </div>
          <img src={IMG_AID} alt="" className="absolute -right-8 -bottom-8 w-40 h-40 rounded-2xl object-cover opacity-40" />
        </aside>
      </section>
    </div>
  );
}
